"use client";

import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";

/** AI stack on an angular intelligence frame — not orbital/round */
const nodes = [
  { label: "AGENTS", sub: "ACT", x: 200, y: 48, accent: "var(--accent)" },
  { label: "MODELS", sub: "REASON", x: 332, y: 118, accent: "var(--cyan)" },
  { label: "MEMORY", sub: "RECALL", x: 332, y: 282, accent: "var(--accent-tertiary)" },
  { label: "TOOLS", sub: "EXECUTE", x: 200, y: 352, accent: "var(--cyan)" },
  { label: "RAG", sub: "GROUND", x: 68, y: 282, accent: "var(--accent)" },
  { label: "POLICY", sub: "GOVERN", x: 68, y: 118, accent: "var(--accent-tertiary)" },
] as const;

function hexPoints(cx: number, cy: number, r: number) {
  return Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 180) * (60 * i - 30);
    return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
  }).join(" ");
}

export function HeroOrbitGraphic({ className }: { className?: string }) {
  const reduced = useReducedMotion();
  const [active, setActive] = useState(0);
  const cx = 200;
  const cy = 200;

  useEffect(() => {
    if (reduced) return;
    const id = window.setInterval(() => setActive((i) => (i + 1) % nodes.length), 2200);
    return () => window.clearInterval(id);
  }, [reduced]);

  return (
    <div className={className}>
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[70%] w-[70%] -translate-x-1/2 -translate-y-1/2 bg-[radial-gradient(ellipse_at_center,color-mix(in_srgb,var(--accent)_24%,transparent)_0%,color-mix(in_srgb,var(--cyan)_12%,transparent)_48%,transparent_72%)] blur-2xl dark:opacity-90" />

      <motion.svg
        viewBox="0 0 400 400"
        className="relative h-full w-full"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        aria-hidden
      >
        <defs>
          <linearGradient id="hg-frame" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.95" />
            <stop offset="55%" stopColor="var(--violet)" stopOpacity="0.5" />
            <stop offset="100%" stopColor="var(--cyan)" stopOpacity="0.95" />
          </linearGradient>
          <linearGradient id="hg-core-fill" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="1" />
            <stop offset="100%" stopColor="var(--cyan)" stopOpacity="0.85" />
          </linearGradient>
          <linearGradient id="hg-spoke" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.1" />
            <stop offset="100%" stopColor="var(--cyan)" stopOpacity="0.7" />
          </linearGradient>
          <filter id="hg-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.8" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Architecture grid — sharp, not circular */}
        {[70, 110, 150, 190, 230, 270, 310, 350].map((x) => (
          <line key={`vx-${x}`} x1={x} y1="40" x2={x} y2="360" stroke="currentColor" className="text-foreground" strokeOpacity="0.045" />
        ))}
        {[70, 110, 150, 190, 230, 270, 310, 350].map((y) => (
          <line key={`hy-${y}`} x1="40" y1={y} x2="360" y2={y} stroke="currentColor" className="text-foreground" strokeOpacity="0.045" />
        ))}

        {/* Outer hexagonal intelligence frame */}
        <motion.polygon
          points={hexPoints(cx, cy, 168)}
          fill="none"
          stroke="url(#hg-frame)"
          strokeWidth="1.25"
          strokeDasharray="5 7"
          opacity="0.55"
          animate={reduced ? undefined : { rotate: 360 }}
          transition={{ duration: 90, repeat: Infinity, ease: "linear" }}
          style={{ transformOrigin: `${cx}px ${cy}px` }}
        />
        <polygon
          points={hexPoints(cx, cy, 138)}
          fill="none"
          stroke="color-mix(in srgb, var(--accent) 38%, transparent)"
          strokeWidth="1.1"
        />
        <polygon
          points={hexPoints(cx, cy, 98)}
          fill="color-mix(in srgb, var(--accent) 6%, transparent)"
          stroke="color-mix(in srgb, var(--cyan) 32%, transparent)"
          strokeWidth="1"
        />

        {/* Diagonal cross-structure */}
        <line x1="90" y1="90" x2="310" y2="310" stroke="color-mix(in srgb, var(--foreground) 8%, transparent)" strokeWidth="1" />
        <line x1="310" y1="90" x2="90" y2="310" stroke="color-mix(in srgb, var(--foreground) 8%, transparent)" strokeWidth="1" />

        {/* Mesh between adjacent nodes */}
        {[
          [0, 1],
          [1, 2],
          [2, 3],
          [3, 4],
          [4, 5],
          [5, 0],
        ].map(([a, b]) => (
          <line
            key={`mesh-${a}-${b}`}
            x1={nodes[a].x}
            y1={nodes[a].y}
            x2={nodes[b].x}
            y2={nodes[b].y}
            stroke="color-mix(in srgb, var(--foreground) 11%, transparent)"
            strokeWidth="1"
            strokeDasharray="3 5"
          />
        ))}

        {/* Angular spokes + signal pulses */}
        {nodes.map((node, i) => (
          <g key={`spoke-${node.label}`}>
            <motion.line
              x1={cx}
              y1={cy}
              x2={node.x}
              y2={node.y}
              stroke="url(#hg-spoke)"
              strokeWidth={i === active ? 1.9 : 1}
              strokeLinecap="square"
              animate={{ opacity: i === active ? 0.95 : 0.28 }}
              transition={{ duration: 0.35 }}
            />
            {!reduced ? (
              <motion.rect
                width="5"
                height="5"
                fill={node.accent}
                filter="url(#hg-glow)"
                animate={{
                  x: [cx - 2.5, node.x - 2.5, cx - 2.5],
                  y: [cy - 2.5, node.y - 2.5, cy - 2.5],
                  opacity: [0, 1, 0],
                  rotate: [0, 180, 360],
                }}
                transition={{
                  duration: 2.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: i * 0.38,
                }}
              />
            ) : null}
          </g>
        ))}

        {/* Hexagonal neural core */}
        <motion.g
          animate={reduced ? undefined : { scale: [1, 1.035, 1] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: `${cx}px ${cy}px` }}
        >
          <polygon
            points={hexPoints(cx, cy, 52)}
            fill="url(#hg-core-fill)"
            filter="url(#hg-glow)"
            opacity="0.95"
          />
          <polygon
            points={hexPoints(cx, cy, 38)}
            fill="color-mix(in srgb, var(--background) 16%, var(--accent))"
            stroke="color-mix(in srgb, var(--cyan) 75%, white)"
            strokeWidth="1.2"
          />

          {/* Inner neural lattice — square dots */}
          {[
            [188, 186],
            [200, 178],
            [212, 186],
            [182, 200],
            [200, 200],
            [218, 200],
            [188, 214],
            [200, 222],
            [212, 214],
          ].map(([x, y], i) => (
            <motion.rect
              key={`core-n-${i}`}
              x={x - (i === 4 ? 2.6 : 1.5)}
              y={y - (i === 4 ? 2.6 : 1.5)}
              width={i === 4 ? 5.2 : 3}
              height={i === 4 ? 5.2 : 3}
              fill={i === 4 ? "var(--cyan)" : i % 2 ? "#fff" : "color-mix(in srgb, var(--cyan) 80%, white)"}
              animate={reduced ? undefined : { opacity: [0.45, 1, 0.45] }}
              transition={{ duration: 1.7, repeat: Infinity, delay: i * 0.1 }}
            />
          ))}
          {[
            [0, 1],
            [1, 2],
            [0, 3],
            [1, 4],
            [2, 5],
            [3, 4],
            [4, 5],
            [3, 6],
            [4, 7],
            [5, 8],
            [6, 7],
            [7, 8],
          ].map(([a, b], i) => {
            const pts = [
              [188, 186],
              [200, 178],
              [212, 186],
              [182, 200],
              [200, 200],
              [218, 200],
              [188, 214],
              [200, 222],
              [212, 214],
            ] as const;
            return (
              <line
                key={`core-l-${i}`}
                x1={pts[a][0]}
                y1={pts[a][1]}
                x2={pts[b][0]}
                y2={pts[b][1]}
                stroke="rgba(255,255,255,0.35)"
                strokeWidth="0.8"
              />
            );
          })}
        </motion.g>

        <text
          x={cx}
          y={cy + 68}
          textAnchor="middle"
          fill="var(--hero-node-label)"
          fontSize="9"
          fontWeight="700"
          letterSpacing="0.2em"
          fontFamily="var(--font-maxridge-mono), ui-monospace, monospace"
        >
          NEURAL CORE
        </text>

        {/* Angular capability nodes */}
        {nodes.map((node, i) => {
          const isActive = i === active;
          return (
            <motion.g
              key={node.label}
              animate={reduced ? undefined : { y: [0, i % 2 === 0 ? -3 : 3, 0] }}
              transition={{ duration: 3.3 + i * 0.15, repeat: Infinity, ease: "easeInOut" }}
            >
              {isActive && !reduced ? (
                <motion.rect
                  x={node.x - 40}
                  y={node.y - 22}
                  width="80"
                  height="44"
                  rx="2"
                  fill="none"
                  stroke={node.accent}
                  strokeWidth="1"
                  initial={{ opacity: 0.55, scale: 0.92 }}
                  animate={{ opacity: 0, scale: 1.12 }}
                  transition={{ duration: 1.35, repeat: Infinity }}
                  style={{ transformOrigin: `${node.x}px ${node.y}px` }}
                />
              ) : null}
              <rect
                x={node.x - 34}
                y={node.y - 17}
                width="68"
                height="34"
                rx="2"
                fill="var(--hero-node-bg)"
                fillOpacity="0.97"
                stroke={node.accent}
                strokeWidth={isActive ? 1.85 : 1.15}
              />
              {/* Corner ticks — tech feel */}
              <path
                d={`M${node.x - 34} ${node.y - 10} V${node.y - 17} H${node.x - 27}`}
                fill="none"
                stroke={node.accent}
                strokeWidth="1.4"
              />
              <path
                d={`M${node.x + 34} ${node.y + 10} V${node.y + 17} H${node.x + 27}`}
                fill="none"
                stroke={node.accent}
                strokeWidth="1.4"
              />
              <rect x={node.x - 23} y={node.y - 3} width="6" height="6" fill={node.accent} filter="url(#hg-glow)" />
              <text
                x={node.x + 6}
                y={node.y - 1}
                textAnchor="middle"
                fill="var(--hero-node-label)"
                fontSize="8.5"
                fontWeight="700"
                letterSpacing="0.1em"
                fontFamily="var(--font-maxridge-display), system-ui, sans-serif"
              >
                {node.label}
              </text>
              <text
                x={node.x + 6}
                y={node.y + 11}
                textAnchor="middle"
                fill="var(--hero-node-sub)"
                fontSize="6"
                letterSpacing="0.14em"
                fontFamily="var(--font-maxridge-mono), ui-monospace, monospace"
              >
                {node.sub}
              </text>
            </motion.g>
          );
        })}

        {/* Status markers — squared */}
        <g>
          <rect
            x="16"
            y="20"
            width="92"
            height="22"
            rx="2"
            fill="var(--hero-status-bg)"
            stroke="color-mix(in srgb, var(--cyan) 45%, transparent)"
          />
          <motion.rect
            x="28"
            y="27"
            width="8"
            height="8"
            fill="var(--success, #10b981)"
            animate={reduced ? undefined : { opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 1.35, repeat: Infinity }}
          />
          <text
            x="44"
            y="35"
            fill="var(--hero-status-label)"
            fontSize="7.5"
            letterSpacing="0.14em"
            fontFamily="var(--font-maxridge-mono), ui-monospace, monospace"
          >
            AI ONLINE
          </text>
        </g>
        <g>
          <rect
            x="276"
            y="358"
            width="108"
            height="22"
            rx="2"
            fill="var(--hero-status-bg)"
            stroke="color-mix(in srgb, var(--accent) 40%, transparent)"
          />
          <text
            x="330"
            y="373"
            textAnchor="middle"
            fill="var(--hero-status-label)"
            fontSize="7.5"
            letterSpacing="0.12em"
            fontFamily="var(--font-maxridge-mono), ui-monospace, monospace"
          >
            AGENTIC STACK
          </text>
        </g>
      </motion.svg>
    </div>
  );
}
